import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';

import BrandLogo from '@/components/BrandLogo';
import { trackProductEvent, usePageAnalytics } from '@/features/analytics';
import {
  PublicPracticeApiError,
  fetchPublicPracticeSong,
} from '@/features/public-practice/api';
import { parsePublicPracticeBootstrap } from '@/features/public-practice/bootstrap';
import PublicPracticePreview from '@/features/public-practice/PublicPracticePreview';
import { publicPracticeDifficultyLabel } from '@/features/public-practice/PublicPracticeSongList';
import type {
  PublicPracticeRight,
  PublicPracticeSong,
} from '@/features/public-practice/types';

import '../styles/public-practice.css';

const MEDIA_UNAVAILABLE_COPY =
  '현재 MR을 재생할 수 없습니다. 본문과 연습 정보는 계속 볼 수 있습니다.';

type RoutePhase = 'loading' | 'ready' | 'not-found' | 'unavailable';

const RIGHT_LABELS: Record<string, string> = {
  composition: '원곡·작곡',
  arrangement: '편곡',
  performance: '실연',
  master: '마스터',
  lyrics: '가사',
};

function formatDuration(durationSec: number): string {
  const minutes = Math.floor(durationSec / 60);
  const seconds = Math.floor(durationSec % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatSectionTime(value: number): string {
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function rightLabel(right: PublicPracticeRight): string {
  return RIGHT_LABELS[right.layer] ?? '기타 권리';
}

export default function PublicPracticeSongRoute() {
  const { locale = '', slug = '' } = useParams<{
    locale: string;
    slug: string;
  }>();
  const location = useLocation();

  return (
    <PublicPracticeSongResource
      key={`${locale}:${slug}:${location.pathname}`}
      locale={locale}
      pathname={location.pathname}
      slug={slug}
    />
  );
}

interface PublicPracticeSongResourceProps {
  locale: string;
  pathname: string;
  slug: string;
}

function PublicPracticeSongResource({
  locale,
  pathname,
  slug,
}: PublicPracticeSongResourceProps) {
  const initialSong = useMemo(
    () => parsePublicPracticeBootstrap({
      locale,
      slug,
      pathname,
    }),
    [locale, pathname, slug],
  );
  const [song, setSong] = useState<PublicPracticeSong | null>(() => initialSong);
  const [phase, setPhase] = useState<RoutePhase>(() => (
    locale !== 'ko' ? 'not-found' : initialSong ? 'ready' : 'loading'
  ));
  const [mediaUnavailable, setMediaUnavailable] = useState(false);

  const analyticsProperties = useMemo(
    () => (song ? { song_key: song.song_key } : undefined),
    [song],
  );
  usePageAnalytics('practice_song', {
    enabled: song !== null,
    properties: analyticsProperties,
  });

  useEffect(() => {
    if (locale !== 'ko') {
      setSong(null);
      setPhase('not-found');
      return;
    }

    const bootstrapSong = parsePublicPracticeBootstrap({
      locale,
      slug,
      pathname,
    });
    if (bootstrapSong) {
      setSong(bootstrapSong);
      setPhase('ready');
    } else {
      setSong(null);
      setPhase('loading');
    }
    setMediaUnavailable(false);

    const controller = new AbortController();
    void fetchPublicPracticeSong(slug, controller.signal)
      .then((apiSong) => {
        if (controller.signal.aborted) {
          return;
        }
        setSong(apiSong);
        setPhase('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        if (error instanceof PublicPracticeApiError && error.status === 404) {
          setSong(null);
          setPhase('not-found');
          setMediaUnavailable(false);
          return;
        }
        if (bootstrapSong) {
          setSong(bootstrapSong);
          setPhase('ready');
          setMediaUnavailable(true);
          return;
        }
        setPhase('unavailable');
        setMediaUnavailable(true);
      });

    return () => controller.abort();
  }, [locale, pathname, slug]);

  useEffect(() => {
    if (song) {
      document.title = song.seo_title;
    } else if (phase === 'not-found') {
      document.title = '페이지를 찾을 수 없습니다 · 지음';
    } else {
      document.title = '공개 연습곡 · 지음';
    }
  }, [phase, song]);

  if (phase === 'not-found') {
    return <div className="route-shell">404 — 공개 연습 곡을 찾을 수 없습니다.</div>;
  }

  if (!song) {
    return (
      <div className="route-shell public-practice-route-state">
        {phase === 'loading'
          ? '공개 연습 곡을 불러오는 중입니다.'
          : MEDIA_UNAVAILABLE_COPY}
      </div>
    );
  }

  return (
    <main
      className="public-practice-page"
      data-route="public-practice-song"
      lang="ko"
    >
      <nav className="public-practice-breadcrumbs" aria-label="현재 위치">
        <Link className="brand-link" to="/ko" aria-label="지음">
          <BrandLogo language="ko" decorative />
        </Link>
        <span aria-hidden="true">/</span>
        <Link to="/ko/practice-songs">공개 연습곡</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/ko/artists/${song.artist_slug}`}>
          {song.artist_name}
        </Link>
        <span aria-hidden="true">/</span>
        <span>{song.title}</span>
      </nav>

      <header className="public-practice-hero">
        <div className="public-practice-hero__copy">
          <p className="public-practice-kicker">
            Jeeum이 새로 제작한 연습용 편곡·녹음
          </p>
          <h1>
            <span>{song.title}</span>
            <span>연습용 MR</span>
          </h1>
          <p className="public-practice-hero__summary">{song.hero_summary}</p>
          <p className="public-practice-hero__introduction">{song.introduction}</p>
        </div>

        <dl className="public-practice-facts" aria-label="곡 정보">
          <div>
            <dt>아티스트</dt>
            <dd>
              <Link to={`/ko/artists/${song.artist_slug}`}>
                {song.artist_name}
              </Link>
            </dd>
          </div>
          <div>
            <dt>발표 연도</dt>
            <dd>{song.release_year}</dd>
          </div>
          <div>
            <dt>장르</dt>
            <dd>{song.genre}</dd>
          </div>
          <div>
            <dt>난이도</dt>
            <dd>{publicPracticeDifficultyLabel(song.difficulty)}</dd>
          </div>
          <div>
            <dt>보컬 음역</dt>
            <dd>{song.vocal_range}</dd>
          </div>
          <div>
            <dt>버전</dt>
            <dd>{song.version_label}</dd>
          </div>
          <div>
            <dt>작곡</dt>
            <dd>{song.composer}</dd>
          </div>
          <div>
            <dt>템포</dt>
            <dd>{song.bpm.toLocaleString('ko-KR')} BPM</dd>
          </div>
          <div>
            <dt>키</dt>
            <dd>키 {song.musical_key}</dd>
          </div>
          <div>
            <dt>길이</dt>
            <dd>{formatDuration(song.duration_sec)}</dd>
          </div>
          <div>
            <dt>연습</dt>
            <dd>{song.practice_kind === 'vocal' ? '보컬 중심' : song.practice_kind}</dd>
          </div>
        </dl>
      </header>

      {mediaUnavailable ? (
        <p className="public-practice-media-notice" role="status">
          {MEDIA_UNAVAILABLE_COPY}
        </p>
      ) : null}

      <PublicPracticePreview
        key={song.song_key}
        song={song}
        onMediaUnavailable={() => setMediaUnavailable(true)}
      />

      <section
        className="public-practice-editorial-section public-practice-section-guide"
        aria-labelledby="public-practice-sections-title"
      >
        <div className="public-practice-section-heading">
          <p>구간 안내</p>
          <h2 id="public-practice-sections-title">짧게 끊어 익힐 지점</h2>
        </div>
        <ol className="public-practice-section-list">
          {song.sections.map((section, index) => (
            <li key={section.key}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3>{section.label}</h3>
                <p>{section.practice_goal}</p>
              </div>
              <time>
                {formatSectionTime(section.start_sec)}–
                {formatSectionTime(section.end_sec)}
              </time>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="public-practice-editorial-section public-practice-method"
        aria-labelledby="public-practice-method-title"
      >
        <div className="public-practice-section-heading">
          <p>10분 루틴</p>
          <h2 id="public-practice-method-title">오늘의 연습 순서</h2>
        </div>
        <ol>
          {song.practice_steps.map((step, index) => (
            <li key={step}>
              <span>{index + 1}</span>
              <p>{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="public-practice-editorial-section public-practice-limitations"
        aria-labelledby="public-practice-limitations-title"
      >
        <div className="public-practice-section-heading">
          <p>연습 전 확인</p>
          <h2 id="public-practice-limitations-title">알려진 한계</h2>
        </div>
        <ul>
          {song.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </section>

      <section
        className="public-practice-editorial-section public-practice-provenance"
        aria-labelledby="public-practice-credits-title"
      >
        <div className="public-practice-section-heading">
          <p>제작과 권리</p>
          <h2 id="public-practice-credits-title">새 연주로 만든 공개 연습 자료</h2>
        </div>

        <dl className="public-practice-credits">
          <div>
            <dt>편곡</dt>
            <dd>편곡 · {song.credits.arranger}</dd>
          </div>
          <div>
            <dt>실연</dt>
            <dd>실연 · {song.credits.performers.join(', ')}</dd>
          </div>
          <div>
            <dt>마스터</dt>
            <dd>마스터 · {song.credits.master_owner}</dd>
          </div>
        </dl>

        <div className="public-practice-rights">
          {song.rights.map((right) => (
            <article key={`${right.layer}-${right.source_url}`}>
              <div>
                <h3>{rightLabel(right)}</h3>
                <p>
                  상태 · {right.status === 'cleared' ? '공개 가능' : right.status}
                </p>
              </div>
              <p>{right.basis}</p>
              <p>
                <a
                  href={right.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  권리 근거 확인
                </a>
                <time dateTime={right.reviewed_at}>
                  검토일 · {right.reviewed_at}
                </time>
              </p>
            </article>
          ))}
        </div>

        <p className="public-practice-qa">
          오디오 확인 {song.audio_qa_at} · 편집 확인 {song.editorial_qa_at}
          {' '}· 공개 {song.published_at}
        </p>
      </section>

      <section className="public-practice-final-cta" aria-label="다음 단계">
        <p>이제 한 구간을 골라 실제 연습실에서 파트와 악보를 함께 보세요.</p>
        <div>
          <Link
            to={song.room_path}
            className="public-practice-primary-action"
          >
            전체 연습실 열기
          </Link>
          <Link
            to="/login"
            className="public-practice-secondary-action"
            onClick={() => trackProductEvent(
              'practice_signup_click',
              { song_key: song.song_key },
              'practice_song',
            )}
          >
            내 곡도 이렇게 만들기
          </Link>
        </div>
      </section>
    </main>
  );
}
